import '../App.css';
import { useMemo, useState, useEffect, useRef } from "react";
import { useAnimation } from "../hooks/useAnimation";
import { useCsvData } from "../features/useCsvData";
import { DeckGlobeMap } from "../components/DeckGlobeMap";
import { TimeSlider } from "../components/TimeSlider/TimeSlider";
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import { FONT_FAMILY, COLORS } from "../theme";
import { createPortal } from "react-dom";
import { GLOBE_VIEW_ID, INITIAL_VIEW_STATE } from "../features/deckGlMapConfig";
import { DeckGlobePaneSubAllotment } from "../components/DeckGlobePaneSubAllotment";
import { LoadRow } from "../components/LoadRow";
import { fetchBitmapImages, preloadImages, fetchBitmapImagesWithError } from "../utils";
import { ContrastSlider } from "../components/ContrastSlider/ContrastSlider";
import { createAdjustedImageElement } from "../utils/imageProcessing";

import Navbar from "../components/Navbar";
import { FusedForm } from "../components/FusedForm";
import { ControlsContainer } from "../components/ControlsContainer";


const NAVBAR_HEIGHT = 100;
const URL_SERVER = '';

// Helper to get the backend proxy URL for an image
const getApiImageUrl = (path: string) => `${URL_SERVER}/api/image?path=${encodeURIComponent(path)}`;

// BITMAP_IMAGES now comes from local file listing

// Add this helper function at the top level (inside the component or outside if you prefer)
const parseFdlmcpParams = (bucketPath: string) => {
  const match = bucketPath.match(/fdlmcp_(\d{4})_(\d{1,2})_(\d{1,2})_(\d+)/);
  if (match) {
    const [, year, month, day, stepHours] = match;
    return {
      year: parseInt(year),
      month: parseInt(month),
      day: parseInt(day),
      stepHours: parseInt(stepHours)
    };
  }
  return null;
};

// Helper function to extract the last namespace from a bucket path
// Examples:
// - gs://bucket/path/to/folder/ -> "folder"
// - gs://bucket/path/to/file.txt -> "file.txt"
// - gs://bucket/path/ -> "path"
// - gs://bucket/ -> ""
const getLastNamespace = (bucketPath: string) => {
  if (!bucketPath || typeof bucketPath !== 'string') return '';

  // Remove trailing slash if present
  const cleanPath = bucketPath.replace(/\/$/, '');

  // Split by '/' and get the last non-empty part
  const parts = cleanPath.split('/').filter(part => part.length > 0);

  if (parts.length === 0) return '';

  // Get the last part
  const lastPart = parts[parts.length - 1];

  // If it's a GCS bucket name (starts with gs://), skip it
  if (lastPart === 'gs:' || lastPart === 'gs:') return '';

  return lastPart;
};

// Helper function to get the title (first part before underscore) from last namespace
// Examples:
// - "BField_20140606_000000" -> "BField"
// - "test_1" -> "test"
// - "model_run_20241201_143022" -> "model"
const getTitleFromNamespace = (bucketPath: string) => {
  const lastNamespace = getLastNamespace(bucketPath);
  if (!lastNamespace) return '';

  // Split by first underscore and take the first part
  const parts = lastNamespace.split('_');
  return parts[0] || '';
};

// Helper function to parse date from the remaining part after first underscore
// Examples:
// - "BField_20140606_000000_30" -> { year: 2014, month: 6, day: 6, hour: 0, minute: 0, second: 0 }
// - "model_run_20241201_143022" -> { year: 2024, month: 12, day: 1, hour: 14, minute: 30, second: 22 }
const parseDateFromNamespace = (bucketPath: string) => {
  const lastNamespace = getLastNamespace(bucketPath);
  if (!lastNamespace) return null;

  // Split by first underscore and take the remaining part
  const parts = lastNamespace.split('_');
  if (parts.length < 2) return null;

  const remainingPart = parts.slice(1).join('_');

  // Try to parse as YYYYMMDD_HHmmSS format
  const dateTimeMatch = remainingPart.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
  if (dateTimeMatch) {
    const [, year, month, day, hour, minute, second] = dateTimeMatch;
    return {
      year: parseInt(year),
      month: parseInt(month),
      day: parseInt(day),
      hour: parseInt(hour),
      minute: parseInt(minute),
      second: parseInt(second)
    };
  }

  // Fallback: try to parse as just YYYYMMDD format
  const dateMatch = remainingPart.match(/(\d{4})(\d{2})(\d{2})/);
  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    return {
      year: parseInt(year),
      month: parseInt(month),
      day: parseInt(day)
    };
  }

  return null;
};

// Helper function to detect mobile devices
const isMobile = () => {
  return window.innerWidth <= 768;
};

export default function HomePage() {
  // Get localPath from query param or default
  const params = new URLSearchParams(window.location.search);
  const defaultLocalPath = params.get('bucketPath') || params.get('localPath') || 'input/demo_output/1em_2_10sch_val6_6h_uq/epoch_epoch_10_ckpt/BField_20140606_000000_30';
  const [bucketPath, setBucketPath] = useState(defaultLocalPath);
  // Local state for the input field, so changes are only applied on button click
  const [bucketPathInput, setBucketPathInput] = useState(defaultLocalPath);
  // For right globe, default to empty unless query param provided
  const defaultLocalPath2 = params.get('bucketPath2') || params.get('localPath2') || '';
  const [bucketPath2, setBucketPath2] = useState(defaultLocalPath2);
  const [bucketPathInput2, setBucketPathInput2] = useState(defaultLocalPath2);

  // Check if anonymized mode is enabled
  const isAnonMode = process.env.REACT_APP_ANON_MODE === 'true' || process.env.ANON_MODE === 'true';
  console.log('Index isAnonMode:', isAnonMode, 'REACT_APP_ANON_MODE:', process.env.REACT_APP_ANON_MODE, 'ANON_MODE:', process.env.ANON_MODE);

  // Check if form should be hidden
  const hideForm = params.get('hideForm')?.toLowerCase() === 'true';

  // Mobile state
  const [isMobileDevice, setIsMobileDevice] = useState(isMobile());

  // Use ref to track the actual bucketPath value and prevent React StrictMode corruption
  const bucketPathRef = useRef(bucketPath);
  useEffect(() => {
    if (typeof bucketPath === 'string' && bucketPath.length >= 1) {
      bucketPathRef.current = bucketPath;
    }
  }, [bucketPath]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobileDevice(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  // Additional safeguard: ensure bucketPath is always valid before API calls
  const safeBucketPath = useMemo(() => {
    if (typeof bucketPath === 'string' && bucketPath.length >= 1) {
      return bucketPath;
    }
    console.error('Invalid bucketPath detected in safeBucketPath:', bucketPath);
    console.error('This might be a React strict mode issue or state corruption');

    // Try to use the ref value as a fallback
    if (bucketPathRef.current && typeof bucketPathRef.current === 'string' &&
      bucketPathRef.current.length >= 1) {
      console.log('Using ref fallback:', bucketPathRef.current);
      return bucketPathRef.current;
    }

    return '';
  }, [bucketPath]);

  // Parse fdlmcp parameters
  const fdlmcpParams = useMemo(() => {
    // First try MCP parsing
    const mcpParams = parseFdlmcpParams(safeBucketPath);
    if (mcpParams) return mcpParams;

    // If MCP parsing fails, try backup date parsing from namespace
    const backupDate = parseDateFromNamespace(safeBucketPath);
    if (backupDate) {
      return {
        ...backupDate,
        stepHours: 30 // Default step hours for backup parsing
      };
    }

    return null;
  }, [safeBucketPath]);

  // Debug logging for fdlmcpParams
  useEffect(() => {
    console.log('Current bucketPath:', bucketPath);
    console.log('Safe bucketPath:', safeBucketPath);
    console.log('Ref bucketPath:', bucketPathRef.current);
    console.log('Parsed fdlmcpParams:', fdlmcpParams);
    console.log('bucketPath type:', typeof bucketPath);
    console.log('bucketPath length:', bucketPath?.length);
    console.log('Left globe title:', getTitleFromNamespace(safeBucketPath));
    console.log('Right globe title:', getTitleFromNamespace(bucketPath2));
    console.log('Backup date parsing:', parseDateFromNamespace(safeBucketPath));
    console.log('Last namespace:', getLastNamespace(safeBucketPath));
  }, [bucketPath, safeBucketPath, fdlmcpParams, bucketPath2]);

  // Validate bucketPath to prevent corruption
  useEffect(() => {
    if (typeof bucketPath !== 'string' || bucketPath.length < 1) {
      console.error('Invalid bucketPath detected:', bucketPath);
      // Reset to a safe default if corrupted
      setBucketPath('');
    }
  }, [bucketPath]);

  // Form parameters for fallback date calculation
  const [formParams, setFormParams] = useState({
    year: 2025,
    month: 5,
    day: 22,
    forecast_shift_hrs: 30
  });

  // Callback to handle when model completes
  const handleModelComplete = (newBucketPath: string) => {
    setBucketPathInput(newBucketPath);
    setBucketPath(newBucketPath);
  };

  const [bitmapImages, setBitmapImages] = useState<string[]>([]);
  // New state for preloaded images for left globe
  const [preloadedImages, setPreloadedImages] = useState<HTMLImageElement[]>([]);

  // State for right globe
  const [bitmapImages2, setBitmapImages2] = useState<string[]>([]);
  // New state for preloaded images for right globe
  const [preloadedImages2, setPreloadedImages2] = useState<HTMLImageElement[]>([]);

  // Add loading state for each LoadRow
  const [loadingLeft, setLoadingLeft] = useState(false);
  const [loadingRight, setLoadingRight] = useState(false);
  const [errorLeft, setErrorLeft] = useState(false);
  const [errorRight, setErrorRight] = useState(false);
  // Toggle for right map suballotment: off by default unless bucketPath2 is provided
  const [showRightMap, setShowRightMap] = useState(!!params.get('bucketPath2'));

  // Contrast adjustment state
  const [minContrast, setMinContrast] = useState(0);
  const [maxContrast, setMaxContrast] = useState(100);
  const [hasUserChangedContrast, setHasUserChangedContrast] = useState(false);

  useEffect(() => {
    if (!safeBucketPath) {
      setErrorLeft(false);
      setBitmapImages([]);
      setLoadingLeft(false);
      return;
    }
    console.log('Calling fetchBitmapImagesWithError with safeBucketPath:', safeBucketPath);
    fetchBitmapImagesWithError(URL_SERVER, safeBucketPath, setBitmapImages, setErrorLeft, setLoadingLeft);
  }, [safeBucketPath]);

  useEffect(() => {
    fetchBitmapImagesWithError(URL_SERVER, bucketPath2, setBitmapImages2, setErrorRight, setLoadingRight);
  }, [bucketPath2]);

  // Preload images for left globe
  useEffect(() => {
    if (bitmapImages.length > 0) {
      const urls = bitmapImages.map(getApiImageUrl);
      preloadImages(urls, setPreloadedImages);
    } else {
      setPreloadedImages([]);
    }
  }, [bitmapImages]);

  // Preload images for right globe
  useEffect(() => {
    if (bitmapImages2.length > 0) {
      const urls = bitmapImages2.map(getApiImageUrl);
      preloadImages(urls, setPreloadedImages2);
    } else {
      setPreloadedImages2([]);
    }
  }, [bitmapImages2]);

  // Track previous bucketPath to detect when loading is done for left
  const prevBucketPathRef = useRef(bucketPath);
  useEffect(() => {
    if (loadingLeft && bucketPath !== prevBucketPathRef.current) {
      setLoadingLeft(false);
      prevBucketPathRef.current = bucketPath;
    }
  }, [bucketPath, loadingLeft]);
  // Also clear loadingLeft when preloadedImages are ready
  useEffect(() => {
    if (loadingLeft && preloadedImages.length > 0) {
      setLoadingLeft(false);
    }
  }, [preloadedImages, loadingLeft]);

  // Track previous bucketPath2 to detect when loading is done for right
  const prevBucketPath2Ref = useRef(bucketPath2);
  useEffect(() => {
    if (loadingRight && bucketPath2 !== prevBucketPath2Ref.current) {
      setLoadingRight(false);
      prevBucketPath2Ref.current = bucketPath2;
    }
  }, [bucketPath2, loadingRight]);

  const [imageIndex, setImageIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(true);
  const [verticalSizes, setVerticalSizes] = useState([250, 750]); // left, right
  const [horizontalSizes, setHorizontalSizes] = useState([800, 200]); // top, bottom right
  const csvData = useCsvData("https://geoml-fused.s3.amazonaws.com/data/heliolab/sunpde/aftmaps_delta_ts_20140101.csv");
  useAnimation(isPlaying, setImageIndex, preloadedImages.length, 200);

  // Add viewState state for DeckGL sync
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);

  // Create adjusted images based on contrast settings - process only current image
  const adjustedPreloadedImages = useMemo(() => {
    if (preloadedImages.length === 0 || !hasUserChangedContrast) {
      return preloadedImages;
    }

    // Process only the current image
    const newImages = [...preloadedImages];
    if (imageIndex >= 0 && imageIndex < preloadedImages.length) {
      try {
        newImages[imageIndex] = createAdjustedImageElement(preloadedImages[imageIndex], minContrast, maxContrast);
      } catch (error) {
        console.warn('Failed to adjust image contrast:', error);
      }
    }
    return newImages;
  }, [preloadedImages, imageIndex, minContrast, maxContrast, hasUserChangedContrast]);

  const adjustedPreloadedImages2 = useMemo(() => {
    if (preloadedImages2.length === 0 || !hasUserChangedContrast) {
      return preloadedImages2;
    }

    // Process only the current image
    const newImages = [...preloadedImages2];
    if (imageIndex >= 0 && imageIndex < preloadedImages2.length) {
      try {
        newImages[imageIndex] = createAdjustedImageElement(preloadedImages2[imageIndex], minContrast, maxContrast);
      } catch (error) {
        console.warn('Failed to adjust image contrast:', error);
      }
    }
    return newImages;
  }, [preloadedImages2, imageIndex, minContrast, maxContrast, hasUserChangedContrast]);

  // Tooltip state for info icon (move above early return)
  const [showTooltip, setShowTooltip] = useState(false);
  const infoIconRef = useRef<HTMLSpanElement>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to clear tooltip timeout
  const clearTooltipTimeout = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  };

  // Helper function to start tooltip timeout
  const startTooltipTimeout = () => {
    clearTooltipTimeout();
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
      tooltipTimeoutRef.current = null;
    }, 3000);
  };

  // 1. Reset imageIndex to 0 when preloadedImages loads
  useEffect(() => {
    if (preloadedImages.length > 0) {
      setImageIndex(0);
    }
  }, [preloadedImages]);

  // Cleanup tooltip timeout on unmount
  useEffect(() => {
    return () => {
      clearTooltipTimeout();
    };
  }, []);

  const [dotCount, setDotCount] = useState(1);
  const hasNoData = bitmapImages.length === 0 || preloadedImages.length === 0;
  // Show loading spinner only when we have a bucketPath and are actively loading
  const isWaitingForData = hasNoData && !errorLeft && !!safeBucketPath && loadingLeft;
  useEffect(() => {
    if (isWaitingForData) {
      const interval = setInterval(() => {
        setDotCount(prev => (prev % 4) + 1);
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isWaitingForData]);
  if (isWaitingForData) {
    const dots = '.'.repeat(dotCount === 4 ? 0 : dotCount);
    return (
      <div style={{ color: COLORS.text, background: COLORS.background, fontFamily: FONT_FAMILY, padding: 40 }}>
        Warming up Active Regions{dots}
      </div>
    );
  }

  // Add logging before return to debug imageIndex and BITMAP_IMAGES sync
  return (
    <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
      {/* Top Navbar/Header - always fixed at the top */}
      <Navbar
        errorLeft={errorLeft}
        errorRight={errorRight}
        bucketPathInput={bucketPathInput}
        bucketPathInput2={bucketPathInput2}
        FONT_FAMILY={FONT_FAMILY}
        COLORS={COLORS}
        hideForm={hideForm}
        isMobileDevice={isMobileDevice}
      />
      {/* Main content below navbar, absolutely positioned */}
      <div
        style={{
          position: "absolute",
          top: NAVBAR_HEIGHT,
          left: 0,
          width: "100vw",
          height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          overflow: "hidden"
        }}
      >
        {isMobileDevice ? (
          // Mobile layout: vertical stack with left pane at bottom
          <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Top: Globe map takes most space */}
            <div style={{ flex: 1, minHeight: 0 }}>
              {hideForm ? (
                <DeckGlobePaneSubAllotment
                  key={`mobile-${adjustedPreloadedImages.length}`}
                  imageIndex={imageIndex}
                  BITMAP_IMAGES={adjustedPreloadedImages}
                  viewState={viewState}
                  onViewStateChange={({ viewState: vs }) => setViewState(vs)}
                  metadataText="Model id: c0e6cdb"
                  metadataLink="https://github.com/FrontierDevelopmentLab/2025-HL-Active-Regions"
                  showLegend={true}
                  title={getTitleFromNamespace(safeBucketPath)}
                />
              ) : (
                <Allotment defaultSizes={[70, 30]} vertical={false}>
                  <DeckGlobePaneSubAllotment
                    key={`mobile-l-${adjustedPreloadedImages.length}`}
                    imageIndex={imageIndex}
                    BITMAP_IMAGES={adjustedPreloadedImages}
                    viewState={viewState}
                    onViewStateChange={({ viewState: vs }) => setViewState(vs)}
                    metadataText="Model id: c0e6cdb"
                    metadataLink="https://github.com/FrontierDevelopmentLab/2025-HL-Active-Regions"
                    showLegend={!showRightMap}
                    title={getTitleFromNamespace(safeBucketPath)}
                  />
                  {showRightMap && (
                    <DeckGlobePaneSubAllotment
                      key={`mobile-r-${adjustedPreloadedImages2.length}`}
                      imageIndex={imageIndex}
                      BITMAP_IMAGES={adjustedPreloadedImages2}
                      viewState={viewState}
                      onViewStateChange={({ viewState: vs }) => setViewState(vs)}
                      metadataText="Model id: c0e6cdb"
                      metadataLink="https://github.com/FrontierDevelopmentLab/2025-HL-Active-Regions"
                      showLegend={true}
                      title={getTitleFromNamespace(bucketPath2)}
                    />
                  )}
                </Allotment>
              )}
            </div>

            {/* Bottom: Left pane content */}
            <div style={{
              background: COLORS.leftPane,
              color: COLORS.text,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingTop: 20,
              paddingBottom: 40,
              fontFamily: FONT_FAMILY,
              position: 'relative',
              overflow: 'auto',
              maxHeight: '40%'
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, letterSpacing: 1.2, fontFamily: FONT_FAMILY, display: 'flex', alignItems: 'center', gap: 12 }}>
                Active Region Forecast
                <span
                  ref={infoIconRef}
                  style={{
                    display: 'inline-block',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: 'transparent',
                    color: COLORS.focusBorder,
                    textAlign: 'center',
                    lineHeight: '20px',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  tabIndex={0}
                  onMouseEnter={() => {
                    clearTooltipTimeout();
                    setShowTooltip(true);
                  }}
                  onMouseLeave={() => !isMobileDevice && startTooltipTimeout()}
                  onFocus={() => {
                    clearTooltipTimeout();
                    setShowTooltip(true);
                  }}
                  onBlur={() => !isMobileDevice && startTooltipTimeout()}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLORS.focusBorder} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </span>
                {showTooltip && infoIconRef.current && createPortal(
                  (() => {
                    const rect = infoIconRef.current!.getBoundingClientRect();
                    const tooltipWidth = 280;
                    const tooltipHeight = 120; // Approximate height
                    const screenWidth = window.innerWidth;
                    const screenHeight = window.innerHeight;

                    // Calculate positioning to avoid clipping on all sides
                    let leftPosition: number;
                    let topPosition: number;

                    // Horizontal positioning
                    if (isMobileDevice) {
                      // Center on mobile
                      leftPosition = Math.max(16, (screenWidth - tooltipWidth) / 2);
                    } else {
                      // Try right side first, then left side if it clips
                      leftPosition = rect.right + 8;
                      if (leftPosition + tooltipWidth > screenWidth - 16) {
                        leftPosition = rect.left - tooltipWidth - 8;
                      }
                      // Ensure it doesn't go off the left side
                      if (leftPosition < 16) {
                        leftPosition = 16;
                      }
                    }

                    // Vertical positioning - prioritize upward positioning on mobile for better space
                    if (isMobileDevice) {
                      // On mobile, try to position above first to avoid bottom clipping
                      topPosition = rect.top - tooltipHeight - 8;
                      // If it clips at the top, position below
                      if (topPosition < 16) {
                        topPosition = rect.bottom + 8;
                      }
                      // If it still clips at bottom, allow it to extend upward beyond screen bounds
                      if (topPosition + tooltipHeight > screenHeight - 16) {
                        topPosition = Math.max(8, rect.top - tooltipHeight - 8);
                      }
                    } else {
                      // Desktop: original logic
                      topPosition = rect.top - 4;
                      // If it would clip at the top, position below the icon
                      if (topPosition < 16) {
                        topPosition = rect.bottom + 8;
                      }
                      // If it would clip at the bottom, position above the icon
                      if (topPosition + tooltipHeight > screenHeight - 16) {
                        topPosition = rect.top - tooltipHeight - 8;
                      }
                    }

                    return (
                      <div
                        style={{
                          width: tooltipWidth,
                          background: 'rgba(30, 30, 40, 255)',
                          color: 'rgba(255, 255, 255, 255)',
                          textAlign: 'left',
                          borderRadius: 6,
                          padding: '12px 16px',
                          position: 'fixed',
                          zIndex: 99999,
                          left: leftPosition,
                          top: topPosition,
                          boxShadow: COLORS.boxShadow,
                          fontSize: 14,
                          fontWeight: 400,
                          pointerEvents: 'auto',
                          whiteSpace: 'normal',
                          lineHeight: 1.4,
                        }}
                      >
                        {/* Close button */}
                        <div style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          cursor: 'pointer',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          background: 'rgba(255, 255, 255, 0.1)',
                          transition: 'background 0.2s ease'
                        }}
                          onClick={() => setShowTooltip(false)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </div>

                        <div style={{ fontWeight: 600, marginBottom: 8, color: COLORS.focusBorder }}>Run Forecast</div>
                        <div style={{ marginBottom: 8 }}>
                          Input a date and click "Run". ARCADE will download AFT data from the SWIR website and generate forecasts up to 30 hours into the future of the selected date.

                        </div>
                        <div style={{ marginBottom: 8 }}>
                          This model simulates the emergence and transport of solar active regions using a hybrid approach that combines physics-based Surface Flux Transport (SFT) with deep learning. <a href="/" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.focusBorder, textDecoration: 'underline' }}>Read more</a>.
                        </div>
                      </div>
                    );
                  })(),
                  document.body
                )}
              </div>

              {/* FusedForm below the SunPDE Model text - hidden on mobile */}
              {!isMobileDevice && (
                <div style={{ width: '90%', margin: '0 auto 8px auto' }}>
                  {hideForm ? (
                    <div style={{ fontSize: 14, color: COLORS.text, padding: '8px 12px', borderRadius: 6, background: COLORS.boxBackground, border: `1px solid ${COLORS.accent}` }}>
                      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: COLORS.accent }}>
                        Model outputs
                      </div>
                      <div style={{ fontSize: 12, lineHeight: 1.3 }}>
                        {fdlmcpParams ? (
                          <>These are the model outputs for {fdlmcpParams.year}-{fdlmcpParams.month.toString().padStart(2, '0')}-{fdlmcpParams.day.toString().padStart(2, '0')} and {fdlmcpParams.stepHours} hours into the future at 15 minute intervals.</>
                        ) : (
                          <>Showing model outputs for default settings. To generate your own results, run the model <a href="/" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.focusBorder, textDecoration: 'underline' }}>here</a>.</>
                        )}
                      </div>
                    </div>
                  ) : (
                    <FusedForm
                      COLORS={COLORS}
                      FONT_FAMILY={FONT_FAMILY}
                      onModelComplete={handleModelComplete}
                      onFormParamsChange={setFormParams}
                    />
                  )}
                </div>
              )}

              {/* Small spacer - only show if FusedForm is visible */}
              {!isMobileDevice && <div style={{ height: '12px' }} />}

              {/* ControlsContainer below the FusedForm */}
              <div style={{ width: '90%', margin: '0 auto 16px auto' }}>
                <ControlsContainer
                  imageIndex={imageIndex}
                  setImageIndex={setImageIndex}
                  isPlaying={isPlaying}
                  setIsPlaying={setIsPlaying}
                  totalImages={bitmapImages.length}
                  fdlmcpParams={fdlmcpParams}
                  formParams={formParams}
                  minContrast={minContrast}
                  maxContrast={maxContrast}
                  onMinChange={(value) => {
                    setMinContrast(value);
                    setHasUserChangedContrast(true);
                  }}
                  onMaxChange={(value) => {
                    setMaxContrast(value);
                    setHasUserChangedContrast(true);
                  }}
                  onReset={() => {
                    setMinContrast(0);
                    setMaxContrast(100);
                    setHasUserChangedContrast(false);
                  }}
                  disabled={bitmapImages.length === 0}
                  isMobileDevice={isMobileDevice}
                />
              </div>

              {/* Storytelling credit - positioned at bottom center */}
              {!isAnonMode && (
                <div style={{
                  textAlign: 'center',
                  fontSize: 24,
                  fontStyle: 'italic',
                  background: 'rgba(45, 39, 15, 0.1)',
                  padding: '6px 10px',
                  borderRadius: 4,
                  maxWidth: '180px',
                  whiteSpace: 'nowrap',
                  marginTop: '8px',
                  marginBottom: '20px'
                }}>
                  <span style={{ color: 'rgba(150, 150, 149, 0.6)', fontWeight: 300, fontStyle: 'italic' }}>
                    <a
                      href="https://www.linkedin.com/in/plinioguzman/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'rgba(150, 150, 149, 0.1)',
                        textDecoration: 'none',
                        borderBottom: 'none',
                        fontWeight: 200,
                        fontStyle: 'italic'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#ffcc00';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(150, 150, 149, 0.59)';
                      }}
                    >
                      <span style={{ fontFamily: "'Love Ya Like A Sister', cursive" }}>
                        Plinio Guzman
                      </span>
                    </a>
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Desktop layout: original allotment structure
          <Allotment
            defaultSizes={verticalSizes}
            onChange={setVerticalSizes}
          >
            {/* Left Pane: Full vertical height */}
            <div style={{ height: "100%", background: COLORS.leftPane, color: COLORS.text, display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 40, fontFamily: FONT_FAMILY, position: 'relative', overflow: 'auto' }}>
              <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 16, letterSpacing: 1.2, fontFamily: FONT_FAMILY, display: 'flex', alignItems: 'center', gap: 12 }}>
                Active Region Forecast
                <span
                  ref={infoIconRef}
                  style={{
                    display: 'inline-block',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: 'transparent',
                    color: COLORS.focusBorder,
                    textAlign: 'center',
                    lineHeight: '22px',
                    fontWeight: 700,
                    fontSize: 16,
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  tabIndex={0}
                  onMouseEnter={() => {
                    clearTooltipTimeout();
                    setShowTooltip(true);
                  }}
                  onMouseLeave={() => startTooltipTimeout()}
                  onFocus={() => {
                    clearTooltipTimeout();
                    setShowTooltip(true);
                  }}
                  onBlur={() => startTooltipTimeout()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.focusBorder} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </span>
                {showTooltip && infoIconRef.current && createPortal(
                  (() => {
                    const rect = infoIconRef.current!.getBoundingClientRect();
                    const tooltipWidth = 280;
                    const tooltipHeight = 120; // Approximate height
                    const screenWidth = window.innerWidth;
                    const screenHeight = window.innerHeight;

                    // Calculate positioning to avoid clipping on all sides
                    let leftPosition: number;
                    let topPosition: number;

                    // Horizontal positioning
                    if (isMobileDevice) {
                      // Center on mobile
                      leftPosition = Math.max(16, (screenWidth - tooltipWidth) / 2);
                    } else {
                      // Try right side first, then left side if it clips
                      leftPosition = rect.right + 8;
                      if (leftPosition + tooltipWidth > screenWidth - 16) {
                        leftPosition = rect.left - tooltipWidth - 8;
                      }
                      // Ensure it doesn't go off the left side
                      if (leftPosition < 16) {
                        leftPosition = 16;
                      }
                    }

                    // Vertical positioning - prioritize upward positioning on mobile for better space
                    if (isMobileDevice) {
                      // On mobile, try to position above first to avoid bottom clipping
                      topPosition = rect.top - tooltipHeight - 8;
                      // If it clips at the top, position below
                      if (topPosition < 16) {
                        topPosition = rect.bottom + 8;
                      }
                      // If it still clips at bottom, allow it to extend upward beyond screen bounds
                      if (topPosition + tooltipHeight > screenHeight - 16) {
                        topPosition = Math.max(8, rect.top - tooltipHeight - 8);
                      }
                    } else {
                      // Desktop: original logic
                      topPosition = rect.top - 4;
                      // If it would clip at the top, position below the icon
                      if (topPosition < 16) {
                        topPosition = rect.bottom + 8;
                      }
                      // If it would clip at the bottom, position above the icon
                      if (topPosition + tooltipHeight > screenHeight - 16) {
                        topPosition = rect.top - tooltipHeight - 8;
                      }
                    }

                    return (
                      <div
                        style={{
                          width: tooltipWidth,
                          background: 'rgba(30, 30, 40, 255)',
                          color: 'rgba(255, 255, 255, 255)',
                          textAlign: 'left',
                          borderRadius: 6,
                          padding: '12px 16px',
                          position: 'fixed',
                          zIndex: 99999,
                          left: leftPosition,
                          top: topPosition,
                          boxShadow: COLORS.boxShadow,
                          fontSize: 14,
                          fontWeight: 400,
                          pointerEvents: 'auto',
                          whiteSpace: 'normal',
                          lineHeight: 1.4,
                        }}
                      >
                        {/* Close button */}
                        <div style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          cursor: 'pointer',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          background: 'rgba(255, 255, 255, 0.1)',
                          transition: 'background 0.2s ease'
                        }}
                          onClick={() => setShowTooltip(false)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </div>

                        <div style={{ fontWeight: 600, marginBottom: 8, color: COLORS.focusBorder }}>Run Forecast</div>
                        <div style={{ marginBottom: 8 }}>
                          Input a date and click "Run". ARCADE will download AFT data from the SWIR website and generate forecasts up to 30 hours into the future of the selected date.

                        </div>
                        <div style={{ marginBottom: 8 }}>
                          This model simulates the emergence and transport of solar active regions using a hybrid approach that combines physics-based Surface Flux Transport (SFT) with deep learning. <a href="/" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.focusBorder, textDecoration: 'underline' }}>Read more</a>.
                        </div>
                      </div>
                    );
                  })(),
                  document.body
                )}
              </div>

              {/* FusedForm below the SunPDE Model text */}
              <div style={{ width: '90%', margin: '0 auto 10px auto' }}>
                {hideForm ? (
                  <div style={{ fontSize: 16, color: COLORS.text, padding: '10px 15px', borderRadius: 8, background: COLORS.boxBackground, border: `1px solid ${COLORS.accent}` }}>
                    <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: COLORS.accent }}>
                      Model outputs
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.4 }}>
                      {fdlmcpParams ? (
                        <>These are the model outputs for {fdlmcpParams.year}-{fdlmcpParams.month.toString().padStart(2, '0')}-{fdlmcpParams.day.toString().padStart(2, '0')} and {fdlmcpParams.stepHours} hours into the future at 15 minute intervals.</>
                      ) : (
                        <>Showing model outputs for default settings. To generate your own results, run the model <a href="/" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.focusBorder, textDecoration: 'underline' }}>here</a>.</>
                      )}
                    </div>
                  </div>
                ) : (
                  <FusedForm
                    COLORS={COLORS}
                    FONT_FAMILY={FONT_FAMILY}
                    onModelComplete={handleModelComplete}
                    onFormParamsChange={setFormParams}
                  />
                )}
              </div>

              {/* Small spacer */}
              <div style={{ height: '20px' }} />

              {/* ControlsContainer below the FusedForm */}
              <div style={{ width: '90%', margin: '0 auto 10px auto' }}>
                <ControlsContainer
                  imageIndex={imageIndex}
                  setImageIndex={setImageIndex}
                  isPlaying={isPlaying}
                  setIsPlaying={setIsPlaying}
                  totalImages={bitmapImages.length}
                  fdlmcpParams={fdlmcpParams}
                  formParams={formParams}
                  minContrast={minContrast}
                  maxContrast={maxContrast}
                  onMinChange={(value) => {
                    setMinContrast(value);
                    setHasUserChangedContrast(true);
                  }}
                  onMaxChange={(value) => {
                    setMaxContrast(value);
                    setHasUserChangedContrast(true);
                  }}
                  onReset={() => {
                    setMinContrast(0);
                    setMaxContrast(100);
                    setHasUserChangedContrast(false);
                  }}
                  disabled={bitmapImages.length === 0}
                  isMobileDevice={isMobileDevice}
                />
              </div>

              {/* Storytelling credit - positioned at bottom left of left pane */}
              {!isAnonMode && (
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  bottom: 40,
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  // fontFamily: 'Georgia Pro Condensed, serif',
                  fontSize: 30,
                  fontStyle: 'italic',
                  background: 'rgba(45, 39, 15, 0.1)',
                  padding: '8px 12px',
                  borderRadius: 6,
                  zIndex: 1000,
                  maxWidth: '200px',
                  whiteSpace: 'nowrap'
                }}>
                  <span style={{ color: 'rgba(150, 150, 149, 0.6)', fontWeight: 300, fontStyle: 'italic' }}>
                    <a
                      href="https://www.linkedin.com/in/plinioguzman/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'rgba(150, 150, 149, 0.1)',
                        textDecoration: 'none',
                        borderBottom: 'none',
                        fontWeight: 200,
                        fontStyle: 'italic'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#ffcc00';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(150, 150, 149, 0.59)';
                      }}
                    >
                      <span style={{ fontFamily: "'Love Ya Like A Sister', cursive" }}>
                        Plinio Guzman
                      </span>
                    </a>
                  </span>
                </div>
              )}


            </div>
            {/* Right: Two horizontal panes - only show when hideForm is false */}
            {!hideForm ? (
              <Allotment vertical defaultSizes={horizontalSizes} onChange={setHorizontalSizes}>
                {/* Top right: Split vertically for two DeckGL maps */}
                <Allotment defaultSizes={[50, 50]} vertical={false}>
                  <DeckGlobePaneSubAllotment
                    key={`l-${adjustedPreloadedImages.length}`}
                    imageIndex={imageIndex}
                    BITMAP_IMAGES={adjustedPreloadedImages}
                    viewState={viewState}
                    onViewStateChange={({ viewState: vs }) => setViewState(vs)}
                    metadataText="Model id: c0e6cdb"
                    metadataLink="https://github.com/FrontierDevelopmentLab/2025-HL-Active-Regions"
                    showLegend={!showRightMap}
                    title={getTitleFromNamespace(safeBucketPath)}
                  />
                  {showRightMap && (
                    <DeckGlobePaneSubAllotment
                      key={`r-${adjustedPreloadedImages2.length}`}
                      imageIndex={imageIndex}
                      BITMAP_IMAGES={adjustedPreloadedImages2}
                      viewState={viewState}
                      onViewStateChange={({ viewState: vs }) => setViewState(vs)}
                      metadataText="Model id: c0e6cdb"
                      metadataLink="https://github.com/FrontierDevelopmentLab/2025-HL-Active-Regions"
                      showLegend={true}
                      title={getTitleFromNamespace(bucketPath2)}
                    />
                  )}

                </Allotment>
                <div style={{
                  width: "100%",
                  background: COLORS.bottomRightPane,
                  color: COLORS.text,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "left",
                  paddingLeft: 20,
                  paddingTop: 20,
                  fontFamily: FONT_FAMILY,
                  flexDirection: "column",
                  height: "100%"
                }}>
                  <LoadRow
                    value={bucketPathInput}
                    onChange={v => {
                      setBucketPathInput(v);
                      setErrorLeft(false);
                    }}
                    onLoad={() => {
                      setLoadingLeft(true);
                      setBucketPath(bucketPathInput);
                    }}
                    loading={loadingLeft}
                    error={errorLeft}
                    placeholder="Local path for left globe"
                    showInfoIcon={true}
                    toggle={showRightMap}
                    onToggle={() => {
                      setShowRightMap(v => {
                        if (!v && !bucketPathInput2) {
                          setBucketPathInput2(bucketPathInput);
                          setBucketPath2(bucketPathInput);
                        }
                        return !v;
                      });
                    }}
                  />
                  {/* Only show right LoadRow if toggle is ON */}
                  {showRightMap && (
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: 16 }}>
                      <LoadRow
                        value={bucketPathInput2}
                        onChange={v => {
                          setBucketPathInput2(v);
                          setErrorRight(false);
                        }}
                        onLoad={() => {
                          setLoadingRight(true);
                          setBucketPath2(bucketPathInput2);
                        }}
                        loading={loadingRight}
                        error={errorRight}
                        placeholder="Local path for right globe"
                        showInfoIcon={false}
                      />
                    </div>
                  )}

                  {/* Other content for the bottom right pane can go here */}
                </div>
              </Allotment>
            ) : (
              // When hideForm is true, show only the left globe in full width
              <DeckGlobePaneSubAllotment
                key={`l-${adjustedPreloadedImages.length}`}
                imageIndex={imageIndex}
                BITMAP_IMAGES={adjustedPreloadedImages}
                viewState={viewState}
                onViewStateChange={({ viewState: vs }) => setViewState(vs)}
                metadataText="Model id: c0e6cdb"
                metadataLink="https://github.com/FrontierDevelopmentLab/2025-HL-Active-Regions"
                showLegend={true}
                title={getTitleFromNamespace(safeBucketPath)}
              />
            )}
          </Allotment>
        )}
      </div>
    </div>
  );
}
